import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Signal,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpinnerModule } from '@jfw/angular/ecommerce/common-components/spinner';
import { AccountService } from '@jfw/angular/shared/services';
import {
  convertDateToPrettyDay,
  convertISOToPrettyTime,
  convertISOToPrettyTimeLocal,
  getShortHandCarrierService,
} from '@jfw/business-logic';
import {
  Carrier,
  DeliveryDays,
  DeliveryVans,
  ParcelCarrier,
  WarehouseLocationType,
} from '@jfw/types';
@Component({
  selector: 'app-shipping-info',
  templateUrl: './shipping-info.component.html',
  styleUrls: ['./shipping-info.component.scss'],
  imports: [
    MatDividerModule,
    CommonModule,
    MatButtonModule,
    MatIcon,
    MatExpansionModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    SpinnerModule,
  ],
})
export class ShippingInfoComponent {
  private accountService: AccountService = inject(AccountService);

  platformAccount = this.accountService._platformAccount;
  currentSelectedWarehouse: WarehouseLocationType | undefined;
  defaultWarehouse = computed(() => {
    const serviceCenter = this.accountService._platformAccount()?.serviceCenter;

    this.currentSelectedWarehouse = this.warehouseOptions.find(
      (option) => option.cityCode === serviceCenter,
    );
    return this.warehouseOptions.find(
      (option) => option.cityCode === serviceCenter,
    );
  });

  preferredCarrier = this.accountService._preferredCarrier;
  returnCutOffs = this.accountService._returnCutOffs;

  parcelInfo: Signal<ParcelCarrier | undefined> = computed(() => {
    const parcelCarrier = this.accountService._parcelCarrier();
    return parcelCarrier;
  });

  groupedCarriers = computed(() => {
    const carriers = this.parcelInfo() ?? [];
    // group by arrival time (or some other row-defining key)
    const groups: Record<
      string,
      {
        upsPri?: Carrier;
        fedexPri?: Carrier;
        upsStd?: Carrier;
        fedexStd?: Carrier;
        upsSat?: Carrier;
        fedexSat?: Carrier;
      }
    > = {};
    for (const carrier of carriers) {
      const key = carrier.arrival; // grouping by arrival time
      if (!groups[key]) groups[key] = {};

      // Next Day Pri
      if (
        carrier.carrier === 'UPS' &&
        carrier.carrierService === 'Next Day Air'
      ) {
        groups[key].upsPri = carrier;
      }
      if (
        carrier.carrier === 'Fedex' &&
        carrier.carrierService === 'Priority Overnight'
      ) {
        groups[key].fedexPri = carrier;
      }

      // Next Day Std
      if (
        carrier.carrier === 'UPS' &&
        carrier.carrierService === 'Next Day Air Saver'
      ) {
        groups[key].upsStd = carrier;
      }
      if (
        carrier.carrier === 'Fedex' &&
        carrier.carrierService === 'Standard Overnight'
      ) {
        groups[key].fedexStd = carrier;
      }

      // Saturday
      if (
        carrier.carrier === 'UPS' &&
        carrier.carrierService === 'Next Day Air Saver'
      ) {
        groups[key].upsSat = carrier;
      }
      if (
        carrier.carrier === 'Fedex' &&
        carrier.carrierService === 'Standard Overnight'
      ) {
        groups[key].fedexSat = carrier;
      }
    }

    return groups;
  });

  groupedCarriersArray = computed(() => {
    const carriers = this.parcelInfo() ?? [];
    // define rows in order
    const rows: Array<{
      label: string;
      ups?: Carrier;
      fedex?: Carrier;
    }> = [
      { label: 'Next Day Priority' },
      { label: 'Next Day Standard' },
      { label: 'Saturday' },
    ];
    for (const carrier of carriers) {
      if (carrier.carrier === 'UPS') {
        if (carrier.carrierService === 'Next Day Air') {
          rows[0].ups = carrier; // Next Day Priority
        }
        if (carrier.carrierService === 'Next Day Air Saver') {
          rows[1].ups = carrier; // Next Day Standard
        }
        if (carrier.carrierService === 'Next Day Air (Saturday)') {
          rows[2].ups = carrier; // Saturday
        }
      }

      if (carrier.carrier === 'Fedex') {
        if (carrier.carrierService === 'Priority Overnight') {
          rows[0].fedex = carrier; // Next Day Priority
        }
        if (carrier.carrierService === 'Standard Overnight') {
          rows[1].fedex = carrier; // Next Day Standard
        }
        if (carrier.carrierService === 'Priority Overnight (Saturday)') {
          rows[2].fedex = carrier; // Saturday
        }
      }
    }

    return rows;
  });

  deliveryDays: Signal<DeliveryDays | undefined> = computed(() => {
    const deliverDays = this.accountService._deliveryDays();
    return deliverDays;
  });

  deliveryVans: Signal<DeliveryVans | undefined> = computed(() => {
    return this.accountService._deliveryVans();
  });

  parcelLoader = computed(() => this.accountService.parcelLoader());
  deliveryVanLoader = computed(() => this.accountService.deliveryVanLoader());
  deliveryDaysLoader = computed(() => this.accountService.deliveryDaysLoader());
  preferredCarrierLoader = computed(() =>
    this.accountService.preferredCarrierLoader(),
  );

  warehouseOptions: WarehouseLocationType[] = [
    { city: 'Visalia', cityCode: 'VI', state: 'CA', timezone: 'PST' },
    { city: 'Anderson', cityCode: 'AN', state: 'IN', timezone: 'EST' },
    { city: 'Salina', cityCode: 'KS', state: 'KS', timezone: 'CST' },
    { city: 'Trenton (Rental)', cityCode: 'TR', state: 'IL', timezone: 'CST' },
    {
      city: 'Trenton (Purchase)',
      cityCode: 'OT',
      state: 'IL',
      timezone: 'CST',
    },
    { city: 'Atlanta', cityCode: 'GA', state: 'GA', timezone: 'EST' },
    { city: 'Richmond', cityCode: 'RI', state: 'VA', timezone: 'EST' },
    { city: 'Seguin', cityCode: 'TX', state: 'TX', timezone: 'CST' },
  ];

  constructor() {
    effect(() => {
      const account = this.platformAccount();
      const warehouse = untracked(() => this.defaultWarehouse()?.cityCode);
      if (account && warehouse) {
        const { accountNumber } = account;
        if (accountNumber) {
          this.accountService.setParcelCarrierInfo(
            warehouse,
            account.address?.zip ?? '',
          );
          this.accountService.setDeliveryDays(warehouse);
          this.accountService.setDeliveryVans(
            account.accountNumber ?? '',
            warehouse,
          );
          this.accountService.setPreferredCarrier(accountNumber);
        }
      }
    });
  }

  ngOnInit() {}

  async selectedWarehouse(warehouseSelected: string) {
    const warehouse = this.warehouseOptions.find((option) => {
      if (option.city === warehouseSelected) {
        return option;
      }
      return undefined;
    });
    if (warehouse && this.platformAccount()) {
      this.currentSelectedWarehouse = warehouse;
      this.accountService.setParcelCarrierInfo(
        warehouse.cityCode,
        this.platformAccount()?.address?.zip ?? '',
      );
      this.accountService.setDeliveryDays(warehouse.cityCode);
      this.accountService.setDeliveryVans(
        this.platformAccount()?.accountNumber ?? '',
        warehouse.cityCode,
      );
    }
  }

  isHotshotTime(hotshotTimeStart: number, hotshotTimeEnd: number) {
    const now = Date.now() / 1000; // current time in ms
    return now >= hotshotTimeStart && now <= hotshotTimeEnd;
  }

  convertDateToPrettyDay(dateString: string) {
    return convertDateToPrettyDay(dateString);
  }

  convertISOToPrettyTime(isoTime: string) {
    return convertISOToPrettyTime(isoTime);
  }

  convertISOToPrettyTimeLocal(isoTime: string, state: string) {
    return convertISOToPrettyTimeLocal(isoTime);
  }

  getShortHandCarrierService(carrierService: string) {
    return getShortHandCarrierService(carrierService);
  }

  getCarrierShipDays(carrierName: string, serviceGroup: string) {
    let days = '';
    this.parcelInfo()?.forEach((parcel) => {
      if (
        parcel.carrier === carrierName &&
        parcel.carrierService === serviceGroup
      ) {
        days = String(parcel.days);
      }
    });
    return days ? `(${days})` : '';
  }

  getCutoffStatus(dayString: string | null | undefined): string {
    const DEFAULT_STATUS = 'not set';

    if (!dayString || dayString.trim() === '') {
      return DEFAULT_STATUS;
    }

    const parts = dayString.split(':');
    if (parts.length < 2) return DEFAULT_STATUS;

    let hour24 = parseInt(parts[0], 10);
    const minutes = parts[1];

    if (isNaN(hour24)) return DEFAULT_STATUS;

    const period = hour24 >= 12 ? 'pm' : 'am';

    let hour12 = hour24 % 12;
    hour12 = hour12 === 0 ? 12 : hour12;

    return `${hour12}:${minutes}${period}`;
  }

  getTimezone() {
    return this.currentSelectedWarehouse
      ? `times in (${this.currentSelectedWarehouse?.timezone})`
      : '';
  }
  /**
   *
   * @param carrierName UP, FX
   * @param serviceGroup GROUND, AIR
   * @returns mat-icon
   */
  getCarrierShipStatus(carrierName: string, serviceGroup: string) {
    let iconInfo = {
      icon: 'fiber_manual_record',
      toolTip: '',
      colorClass: 'green',
    };
    this.deliveryDays()?.forEach((carrier) => {
      if (
        carrierName === carrier.carrier &&
        serviceGroup === carrier.serviceGroup
      ) {
        const status = carrier.shipStatus;
        if (status === 'Open') {
          iconInfo.colorClass = 'green';
          iconInfo.toolTip =
            carrier.message === 'Unknown' ? '' : carrier.message;
        }
        if (status === 'Closed') {
          iconInfo.colorClass = 'red';
          iconInfo.toolTip = 'Closed';
        }
        if (status === 'Hot Shot') {
          iconInfo.colorClass = 'yellow';
          iconInfo.toolTip = carrier.message;
        }
      }
    });

    return iconInfo;
  }
}
